import React from "react";
import { useNavigate } from "react-router-dom";

const CrossChainBridge = () => {
  const navigate = useNavigate();

  const supportedChains = [
    { name: "Ethereum", icon: "🔷", status: "Active", fees: "0.1%" },
    { name: "Binance Smart Chain", icon: "💲", status: "Active", fees: "0.08%" },
    { name: "Polygon", icon: "🔶", status: "Active", fees: "0.05%" },
    { name: "Avalanche", icon: "❄️", status: "Active", fees: "0.1%" },
    { name: "Arbitrum", icon: "⚡", status: "Active", fees: "0.07%" },
    { name: "Optimism", icon: "🔴", status: "Active", fees: "0.07%" },
    { name: "Solana", icon: "🌀", status: "Coming Soon", fees: "TBD" },
    { name: "Cardano", icon: "🔵", status: "Coming Soon", fees: "TBD" }
  ];

  const bridgeStats = [
    { label: "Total Volume", value: "$128.4M", change: "+45%" },
    { label: "Transactions", value: "284.2K", change: "+32%" },
    { label: "Supported Chains", value: "6", change: "+2" },
    { label: "Gas Saved", value: "8.7M XEC", change: "+28%" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-eco-black to-eco-dark pt-20">
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-eco-green hover:text-eco-blue mb-8 transition-colors"
        >
          ← Back to Ecosystem
        </button>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-gradient-to-br from-eco-green to-eco-blue rounded-3xl flex items-center justify-center text-eco-black text-3xl mx-auto mb-6">
            🌐
          </div>
          <h1 className="text-5xl font-black gradient-text mb-4">Cross-Chain Bridge</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Seamlessly transfer assets between networks with our eco-conscious bridging solution. 
            Experience multi-chain interoperability with minimal environmental impact.
          </p>
        </div>

        {/* Bridge Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {bridgeStats.map((stat, index) => (
            <div key={index} className="glass-card p-6 rounded-2xl text-center border border-eco-green border-opacity-20">
              <div className="text-2xl font-black text-white mb-2">{stat.value}</div>
              <div className="text-gray-400 text-sm mb-1">{stat.label}</div>
              <div className="text-eco-green text-sm font-bold">{stat.change}</div>
            </div>
          ))}
        </div>

        {/* Supported Chains */}
        <div className="glass-card rounded-3xl p-8 mb-16 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Supported Networks</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {supportedChains.map((chain, index) => (
              <div key={index} className="bg-eco-dark rounded-2xl p-6 border border-gray-700 hover:border-eco-green transition-all duration-300 group text-center">
                <div className="text-3xl mb-3 group-hover:scale-110 transition-transform duration-300">
                  {chain.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{chain.name}</h3>
                <div className={`text-sm font-semibold mb-2 ${
                  chain.status === "Active" ? "text-eco-green" : "text-yellow-400"
                }`}>
                  {chain.status}
                </div>
                <div className="text-sm text-gray-400">Fees: {chain.fees}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="glass-card p-6 rounded-3xl border border-eco-green border-opacity-20 text-center">
            <div className="w-16 h-16 bg-eco-green rounded-2xl flex items-center justify-center text-eco-black text-2xl mx-auto mb-4">
              🔄
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Multi-Chain Support</h3>
            <p className="text-gray-300">
              Connect to multiple blockchain networks with a single interface. Expand your DeFi opportunities across ecosystems.
            </p>
          </div>
          <div className="glass-card p-6 rounded-3xl border border-eco-green border-opacity-20 text-center">
            <div className="w-16 h-16 bg-eco-blue rounded-2xl flex items-center justify-center text-eco-black text-2xl mx-auto mb-4">
              🛡️
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Secure Transfers</h3>
            <p className="text-gray-300">
              Advanced security protocols and multi-signature verification ensure your assets are always protected during transfers.
            </p>
          </div>
          <div className="glass-card p-6 rounded-3xl border border-eco-green border-opacity-20 text-center">
            <div className="w-16 h-16 bg-eco-purple rounded-2xl flex items-center justify-center text-eco-black text-2xl mx-auto mb-4">
              🌱
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Energy Optimized</h3>
            <p className="text-gray-300">
              Our bridging solution uses 85% less energy than traditional cross-chain protocols through optimized smart contracts.
            </p>
          </div>
        </div>

        {/* Bridge Demo */}
        <div className="glass-card rounded-3xl p-8 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Bridge Your Assets</h2>
          <div className="max-w-2xl mx-auto">
            <div className="bg-eco-dark rounded-2xl p-6 mb-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">From Network</label>
                  <select className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-eco-green">
                    <option>Ethereum</option>
                    <option>Binance Smart Chain</option>
                    <option>Polygon</option>
                    <option>Avalanche</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">To Network</label>
                  <select className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-eco-green">
                    <option>X EcoChain</option>
                    <option>Polygon</option>
                    <option>Binance Smart Chain</option>
                    <option>Avalanche</option>
                  </select>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Amount</label>
                <div className="flex">
                  <input 
                    type="number" 
                    placeholder="0.0"
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-l-xl px-4 py-3 text-white focus:outline-none focus:border-eco-green"
                  />
                  <select className="bg-gray-600 border border-gray-600 rounded-r-xl px-4 py-3 text-white focus:outline-none focus:border-eco-green">
                    <option>ETH</option>
                    <option>USDC</option>
                    <option>USDT</option>
                    <option>XEC</option>
                  </select>
                </div>
              </div>
              <div className="text-center text-sm text-gray-400 mb-4">
                Bridge Fee: <span className="text-eco-green">0.08%</span> • Estimated Gas: <span className="text-eco-green">$1.24</span>
              </div>
              <button className="w-full py-4 bg-gradient-to-r from-eco-green to-eco-blue text-eco-black font-bold rounded-xl hover:opacity-90 transition-opacity">
                Bridge Assets
              </button>
            </div>
            <div className="text-center text-gray-400 text-sm">
              Average bridge time: 2-5 minutes • 24/7 support available
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossChainBridge;